// ComposeDialog.jsx

import { BrandButton, TextCancelButton } from '../button';
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { emailDialogContentSx } from './emailDialogShared';
import { MailAttachmentDropArea, MailAttachmentChipList } from './MailAttachmentFields';
import PageSnackbars from '../common/PageSnackbars';
import { usePageSnackbar } from '../common/usePageSnackbar';

const ComposeDialog = ({
  open,
  onClose,
  mode,          // 'new' | 'reply'
  accountId,
  defaultTo = '',
  defaultSubject = '',
  onSuccess
}) => {
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState(mode === 'reply' ? (defaultSubject || 'Re: ') : '');
  const [editorContent, setEditorContent] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const { setSuccessMessage, setErrorMessage, snackbarProps } = usePageSnackbar();

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachedFiles((prev) => [...prev, ...files]);
    setFileInputKey(Date.now());
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setAttachedFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!accountId) return;
    const formData = new FormData();
    formData.append('to', to);
    formData.append('subject', subject);
    formData.append('htmlContent', editorContent);
    attachedFiles.forEach((file) => {
      formData.append('attachments', file);
    });

    try {
      await axios.post(`/api/mail/messages/${accountId}/send`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccessMessage('Email sent successfully!');
      onClose();
      if (onSuccess) onSuccess('Email sent successfully!');
      setTo('');
      setSubject(mode === 'reply' ? (defaultSubject || 'Re: ') : '');
      setEditorContent('');
      setAttachedFiles([]);
      setFileInputKey(Date.now());
    } catch (error) {
      console.error('Error sending email:', error);
      setErrorMessage('Failed to send email.');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          {mode === 'new' ? 'New Message' : 'Reply'}
        </DialogTitle>
        <DialogContent sx={emailDialogContentSx}>
          <TextField
            label="To"
            fullWidth
            margin="dense"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <TextField
            label="Subject"
            fullWidth
            margin="dense"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <ReactQuill
            theme="snow"
            value={editorContent}
            onChange={setEditorContent}
            style={{ height: 200, marginTop: 10, marginBottom: 20 }}
          />

          <MailAttachmentDropArea
            fileInputKey={fileInputKey}
            onFileChange={handleFileChange}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          />
          <MailAttachmentChipList files={attachedFiles} onRemove={handleRemoveFile} />
        </DialogContent>
        <DialogActions>
          <TextCancelButton onClick={onClose}>Cancel</TextCancelButton>
          <BrandButton onClick={handleSend}>
            Send
          </BrandButton>
        </DialogActions>
      </Dialog>

      <PageSnackbars {...snackbarProps} />
    </>
  );
};

export default ComposeDialog;
